import { useState } from 'react';

import PropTypes from 'prop-types';

import Panel from '#globalComponents/Overlay/Panel';
import Popover from '#globalComponents/Overlay/Popover';
import Tag from '#globalComponents/Tag/Tag';

/**
 * "+N" pill that opens a popover listing the overflow tags. Keeps the table row at a fixed height
 * (the cell no longer wraps) while still exposing every tag on demand. The pill shape signals
 * interactivity. Click is stopped from bubbling so opening the popover (or removing a tag inside
 * it) does not trigger the surrounding row click. When `onRemoveTag` is given, each tag in the
 * popover shows a ✕ to detach it from the company.
 */
const TagOverflowPopover = ({ tags, count, onRemoveTag }) => {
	const [open, setOpen] = useState(false);

	const toggle = (e) => {
		e.stopPropagation();
		setOpen((prev) => !prev);
	};

	const trigger = (
		<button
			type="button"
			onClick={toggle}
			className={`inline-flex items-center rounded-full border px-1.5 py-0.5 text-[10px] font-medium transition-colors ${
				open
					? 'border-bgBase-600 bg-bgBase-400 text-text'
					: 'border-bgBase-500 bg-bgBase-300 text-bgBase-800 hover:bg-bgBase-400 hover:text-text'
			}`}
			title={tags.map((t) => t.name).join(', ')}
		>
			+{count}
		</button>
	);

	return (
		<Popover open={open} onClose={() => setOpen(false)} trigger={trigger} size="s" align="start">
			<Panel.Body>
				<div className="flex flex-wrap gap-1" onClick={(e) => e.stopPropagation()}>
					{tags.map((tag) => (
						<Tag
							key={tag.id}
							label={tag.name}
							size="xs"
							hoverRemove={Boolean(onRemoveTag)}
							onRemove={onRemoveTag ? () => onRemoveTag(tag.id) : undefined}
						/>
					))}
				</div>
			</Panel.Body>
		</Popover>
	);
};

TagOverflowPopover.propTypes = {
	tags: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
			name: PropTypes.string.isRequired,
		}),
	).isRequired,
	count: PropTypes.number.isRequired,
	onRemoveTag: PropTypes.func,
};

export default TagOverflowPopover;
